import { useEffect, useMemo } from 'react';
import { Typography } from '@vp/swan';
import styled from 'styled-components';
import { useTypewriter } from '../../hooks/useTypewriter';
import { parseToWordTokens } from './inlineMarkdown';

const RevealedWord = styled.span`
    transition: opacity 200ms ease-in;

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }
`;

export type TypewriterTextProps = {
    text: string;
    /** Called once every word is visible, so the caller can render the settled prose instead. */
    onRevealComplete?: () => void;
};

/**
 * Reveals prose word by word.
 *
 * Every word is in the DOM from the first render and only its `opacity` changes, so the
 * bubble occupies its final height immediately. Revealing by inserting words instead
 * would grow the bubble under a viewport the thread has already scrolled to the bottom of.
 * Line breaks are the one exception and appear straight away, because revealing them
 * progressively makes multi-paragraph replies jump as each one lands.
 *
 * This component is mounted only while the reveal is running: the per-word wrappers split
 * the prose across elements, which breaks text selection and any query that reads a single
 * text node, so the caller swaps back to the plain markup the moment the reveal finishes.
 */
export function TypewriterText({ text, onRevealComplete }: TypewriterTextProps) {
    const tokens = useMemo(() => parseToWordTokens(text), [text]);
    const { visibleCount, complete } = useTypewriter(tokens.length);

    useEffect(() => {
        if (complete) {
            onRevealComplete?.();
        }
    }, [complete, onRevealComplete]);

    return (
        <Typography style={{ wordBreak: 'break-word' }}>
            {tokens.map((token, index) => {
                if (token.kind === 'newline') {
                    return <br key={index} />;
                }

                const spaceFollows = tokens[index + 1]?.kind !== 'newline';

                return (
                    <RevealedWord key={index} style={{ opacity: index < visibleCount ? 1 : 0 }}>
                        {token.bold ? <strong>{token.text}</strong> : token.text}
                        {spaceFollows ? ' ' : null}
                    </RevealedWord>
                );
            })}
        </Typography>
    );
}
